import { useState } from "react";
import logo from "@assets/img/logo/logo-simple.png";
import { Button } from "@components/buttons/button";
import { useAuthContext } from "@hooks/contexts/auth-context";
import { config } from "config";
import {
  FaArrowRight,
  FaBars,
  FaSignOutAlt,
  FaTicketAlt,
  FaTimes,
} from "react-icons/fa";

type LandingHeaderProps = {
  isAuthenticated: boolean;
  onGoHome: () => void;
  onGoLogin: () => void;
  onGoEntry: () => void;
};

const navItems = [
  { label: "Recursos", target: "recursos" },
  { label: "Modelos", target: "modelos-campanha" },
  { label: "Parceiros", target: "parceiros" },
  { label: "Como funciona", target: "como-funciona" },
  { label: "Preços", target: "modelos-campanha" },
];

export function LandingHeader({
  isAuthenticated,
  onGoHome,
  onGoLogin,
  onGoEntry,
}: LandingHeaderProps) {
  const { signOut } = useAuthContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  function scrollTo(target: string) {
    document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
    setIsMenuOpen(false);
  }

  function openStore() {
    setIsMenuOpen(false);
  }

  function goLogin() {
    setIsMenuOpen(false);
    onGoLogin();
  }

  function goEntry() {
    setIsMenuOpen(false);
    onGoEntry();
  }

  function handleSignOut() {
    setIsMenuOpen(false);
    signOut();
  }

  const linkHover = {
    fontSize: 15,
    opacity: 0.86,
    transition: "opacity .18s ease, transform .18s ease, color .18s ease",
  };

  return (
    <header
      className="landing-header border-bottom"
      style={{
        background:
          "linear-gradient(90deg, #081321 0%, #101d2e 48%, #071423 100%)",
        borderColor: "rgba(255,255,255,.1)",
        boxShadow: "0 10px 32px rgba(3, 12, 24, .28)",
      }}
    >
      <div className="container px-3 px-sm-4 py-2">
        <div className="d-flex align-items-center justify-content-between gap-3">
          <button
            type="button"
            className="btn p-0 border-0 d-flex align-items-center flex-shrink-0"
            onClick={onGoHome}
            title="Voltar para a página inicial."
            style={{ transition: "transform .18s ease, filter .18s ease" }}
            onMouseEnter={(event) => {
              event.currentTarget.style.transform =
                "translateY(-1px) scale(1.04)";
              event.currentTarget.style.filter =
                "drop-shadow(0 0 12px rgba(13,110,253,.38))";
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.transform = "none";
              event.currentTarget.style.filter = "none";
            }}
          >
            <img
              className="landing-header-logo"
              src={logo}
              style={{ height: "6rem" }}
              alt={config.appName}
            />
            <span className="text-white ms-1 fs-5">{config.appName}</span>
          </button>

          <nav className="d-none d-lg-flex align-items-center gap-4 fw-semibold">
            {navItems.map((item) => (
              <button
                type="button"
                className="btn btn-link px-1 py-2 text-white text-decoration-none"
                onClick={() => scrollTo(item.target)}
                key={item.label}
                style={linkHover}
                onMouseEnter={(event) => {
                  event.currentTarget.style.opacity = "1";
                  event.currentTarget.style.transform = "translateY(-1px)";
                  event.currentTarget.style.color = "#8fc2ff";
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.opacity = ".86";
                  event.currentTarget.style.transform = "none";
                  event.currentTarget.style.color = "#fff";
                }}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="d-none d-lg-flex align-items-center gap-3 flex-shrink-0">
            <a
              className="btn btn-success rounded-4 text-white px-4 py-3 fw-bold shadow-sm"
              href={config.urls.store}
              target="_blank"
              rel="noreferrer"
              title="Abrir a loja onde os clientes compram tickets."
              onClick={openStore}
            >
              <FaTicketAlt className="me-2" />
              Explorar
            </a>

            {isAuthenticated ? (
              <>
                <Button
                  className="btn btn-primary rounded-4 text-white px-4 py-3 shadow-sm"
                  onClick={goEntry}
                >
                  Dashboard
                  <FaArrowRight className="ms-2" />
                </Button>
                <button
                  type="button"
                  className="btn btn-outline-danger rounded-4 px-3 py-3"
                  onClick={handleSignOut}
                  title="SAIR"
                  aria-label="SAIR"
                >
                  <FaSignOutAlt />
                </button>
              </>
            ) : (
              <Button
                className="btn btn-primary rounded-4 text-white px-5 py-3 shadow-sm"
                onClick={goLogin}
              >
                Entrar | Cadastrar
                <FaArrowRight className="ms-2" />
              </Button>
            )}
          </div>

          <button
            type="button"
            className="btn btn-outline-light rounded-4 d-lg-none px-3 py-2"
            onClick={() => setIsMenuOpen((current) => !current)}
            title="Mostrar opções"
            aria-label="Mostrar opções"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        {isMenuOpen && (
          <div className="landing-mobile-menu d-lg-none">
            <div className="d-flex flex-column gap-2 landing-mobile-menu-content">
              {navItems.map((item) => (
                <button
                  type="button"
                  className="landing-mobile-nav-item text-white text-start text-decoration-none fw-semibold"
                  onClick={() => scrollTo(item.target)}
                  key={item.label}
                >
                  {item.label}
                </button>
              ))}

              <a
                className="btn btn-success rounded-4 text-white py-3 fw-bold mt-2"
                href={config.urls.store}
                target="_blank"
                rel="noreferrer"
                title="Abrir a loja onde os clientes compram tickets."
                onClick={openStore}
              >
                <FaTicketAlt className="me-2" />
                Explorar
              </a>

              {isAuthenticated ? (
                <>
                  <Button
                    className="btn btn-primary rounded-4 text-white py-3"
                    onClick={goEntry}
                  >
                    Dashboard
                    <FaArrowRight className="ms-2" />
                  </Button>
                  <button
                    type="button"
                    className="btn btn-outline-danger rounded-4 py-3"
                    onClick={handleSignOut}
                    title="SAIR"
                    aria-label="SAIR"
                  >
                    <FaSignOutAlt />
                  </button>
                </>
              ) : (
                <Button
                  className="btn btn-primary rounded-4 text-white py-3"
                  onClick={goLogin}
                >
                  Entrar | Cadastrar
                  <FaArrowRight className="ms-2" />
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
